import React, {useEffect} from "react";
import {DEFAULT_STATE, PaxNotifierReducer} from "./state";
import PaxNotifierForm, {FormValues, PaxNotifierFormOffsetOptions} from "./form";
import {PaxNotifierPaxRecord} from "../../types";
import {Moment} from "moment-timezone";
import {useSendCheckInNotificationMutation} from "./graphql";
import {message} from "antd";
import {PaxNotifierDrawerContext} from "../../drawer/drawer";


function useCheckInPaxNotifierState(paxData: PaxNotifierPaxRecord[], refetchAfterSave?: () => Promise<PaxNotifierPaxRecord[]>){
    const [ state, dispatch ] = React.useReducer(PaxNotifierReducer, DEFAULT_STATE);
    const hasSetDefaultSelectedPax = React.useRef(false);

    // Keep the latest paxData available for callbacks invoked after async work (avoids stale closures)
    const paxDataRef = React.useRef(paxData);
    const selectPaxIdsRef = React.useRef(state.selectedPaxIds);
    useEffect(() => {
        paxDataRef.current = paxData;
        selectPaxIdsRef.current = state.selectedPaxIds;
    }, [paxData, state.selectedPaxIds]);

    const { sendCheckInNotification, result } = useSendCheckInNotificationMutation();

    // Default to all pax with no previous notification selected
    useEffect(() => {
        if (paxData.length && !hasSetDefaultSelectedPax.current){
            const filteredPax = paxData.filter(p => !p.lastCheckInNotificationEvent?.success);
            dispatch({ type: 'SET_SELECTED_PAX_IDS', payload: filteredPax.map(p => p._id) })
            hasSetDefaultSelectedPax.current = true;
        }
    }, [paxData]);

    // De-selects pax that are already notified
    const deselectNotifiedPax = React.useCallback(() => {
        const latestPaxData = paxDataRef.current;
        const filteredPax = latestPaxData.filter(p => !p.lastCheckInNotificationEvent?.success);
        const filteredIds = filteredPax.map(p => p._id);
        const newSelectedIds = selectPaxIdsRef.current.filter(id => filteredIds.includes(id));
        dispatch({ type: 'SET_SELECTED_PAX_IDS', payload: newSelectedIds });
    }, []);

    const paxWithNotifications = paxData.filter(p => p.lastCheckInNotificationEvent?.success).length;
    const selectedPaxWithNotifications = paxData
        .filter(p => state.selectedPaxIds.includes(p._id))
        .filter(p => p.lastCheckInNotificationEvent?.success);


    return {
        state,
        paxWithNotifications,
        selectedPaxWithNotifications,
        setSelectedPaxIds: (paxIds: string[]) => {
            dispatch({
                type: 'SET_SELECTED_PAX_IDS',
                payload: paxIds
            })
        },
        deselectNotifiedPax,
        sendCheckInNotificationResult: result,
        onFormSubmit: (formValues: FormValues) => {
            sendCheckInNotification(state.selectedPaxIds, formValues.requestedCheckInTime)
                .then(r => {
                    // Close the drawer on successful save
                    if (r.data) {
                        // TODO: Handle partial failures better
                        if (r.data.failedCount > 0){
                            message.warning("Some notifications failed to send. Successful: " + r.data.successfulCount + ", Failed: " + r.data.failedCount);
                        }
                        else if (r.data.successfulCount === 0){
                            message.error("No notifications were sent successfully.");
                        }
                        else{
                            message.success("Check-in notifications sent successfully to " + r.data.successfulCount + " passenger(s).");
                        }
                        refetchAfterSave?.()
                            // After updating paxData from external source, de-select notified pax.
                            .then((pax) => {
                                if (pax){
                                    paxDataRef.current = pax;
                                }
                                deselectNotifiedPax();
                            })
                    }
                })
                .catch((e) => {
                    console.error("Error sending check-in notifications:", e);
                    message.error("Failed to send check-in notifications.");
                })
        }
    }
}

export const CheckInPaxNotifierContext = React.createContext<ReturnType<typeof useCheckInPaxNotifierState> | null>(null);

export type CheckInPaxNotifierProps = {

    /**
     * If set, pre-fill the expected check-in time field with this value and disable user input.
     */
    requestedCheckInTime?: Moment,

    paxData: PaxNotifierPaxRecord[],
    scheduledFlightDate?: Moment,

    refetchAfterSave?: () => Promise<PaxNotifierPaxRecord[]>,
} & PaxNotifierFormOffsetOptions

export default function CheckinPaxNotifier(props: CheckInPaxNotifierProps){

    const context = useCheckInPaxNotifierState(props.paxData, props.refetchAfterSave);

    return <div>
        <CheckInPaxNotifierContext.Provider value={context}>
            <PaxNotifierForm
                offsetMinutesOptions={props.offsetMinutesOptions}
                requestedCheckInTime={props.requestedCheckInTime}
                paxDocs={props.paxData}
                scheduledFlightDate={props.scheduledFlightDate}
            />
        </CheckInPaxNotifierContext.Provider>
    </div>
}